// Same-origin by default ('/api' → nginx reverse-proxies to the backend in
// prod; Vite dev-server proxy handles it locally). Override with VITE_API_BASE
// at build time if the backend lives on a different origin.
const API_BASE = import.meta.env.VITE_API_BASE || '/api';

// Shared across concurrent callers so an expired session triggers exactly one
// refresh, not one per in-flight request.
let refreshInFlight = null;
let refreshedToken = null;

const isTokenExpired = (tok) => {
  if (!tok) return true;
  try {
    const parts = tok.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    // Consider expired if within 15 seconds of expiry
    return typeof payload.exp === 'number' && (payload.exp * 1000) <= (Date.now() + 15000);
  } catch {
    return false;
  }
};

const handleForceLogout = () => {
  refreshInFlight = null;
  refreshedToken = null;
  localStorage.removeItem('org.accessToken');
  localStorage.removeItem('org.refreshToken');
  localStorage.removeItem('org.user');
  localStorage.removeItem('org.org');
  localStorage.setItem('org.view', 'login');
  localStorage.setItem('org.activeTab', 'dashboard');
  window.location.reload();
};

const refreshAuthToken = async () => {
  const refreshToken = localStorage.getItem('org.refreshToken');
  if (!refreshToken) {
    handleForceLogout();
    return null;
  }

  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    }).finally(() => {
      setTimeout(() => { refreshInFlight = null; }, 0);
    });
  }

  try {
    const refreshRes = await refreshInFlight;
    if (refreshRes.ok) {
      if (!refreshedToken) {
        refreshedToken = refreshRes.clone().json().then((d) => {
          localStorage.setItem('org.accessToken', d.accessToken);
          return d.accessToken;
        }).finally(() => {
          setTimeout(() => { refreshedToken = null; }, 0);
        });
      }
      return await refreshedToken;
    } else {
      handleForceLogout();
      return null;
    }
  } catch (err) {
    console.error('Failed to refresh token:', err);
    return null;
  }
};

export const fetchWithAuth = async (endpoint, options = {}) => {
  let token = localStorage.getItem('org.accessToken');
  
  if (!token) {
    try {
      const orgUser = JSON.parse(localStorage.getItem('org.user'));
      if (orgUser && orgUser.accessToken) {
        token = orgUser.accessToken;
        localStorage.setItem('org.accessToken', token);
        if (orgUser.refreshToken) {
          localStorage.setItem('org.refreshToken', orgUser.refreshToken);
        }
      }
    } catch (e) {
      console.error("Failed to restore token from org.user", e);
    }
  }

  // Proactive refresh: if token is expired or missing, refresh before firing request
  if (!token || isTokenExpired(token)) {
    const refreshToken = localStorage.getItem('org.refreshToken');
    if (refreshToken) {
      const freshToken = await refreshAuthToken();
      if (freshToken) token = freshToken;
    }
  }

  const makeRequest = (overrideToken) => {
    const activeToken = overrideToken || token;
    const headers = { 
      'Cache-Control': 'no-cache',
      ...options.headers 
    };
    if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (activeToken) {
      headers['Authorization'] = `Bearer ${activeToken}`;
    }
    return fetch(`${API_BASE}${endpoint}`, { 
      cache: 'no-store',
      ...options, 
      headers 
    });
  };

  let response = await makeRequest();

  // If still unauthorized, attempt to refresh the token as fallback
  if (response.status === 401) {
    const freshToken = await refreshAuthToken();
    if (freshToken) {
      token = freshToken;
      response = await makeRequest(freshToken);
    }
  }

  return response;
};
